import classNames from 'classnames';
import { memo, useEffect } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { useSelector } from 'react-redux';
import { GerenciadorPrecos } from '../../../@types/GerenciadorPrecos';
import { searchSegmentos } from '../../../actions/actionsRevisao';
import NewFilterBox from '../../../components/NewFilterBox/NewFilterBox';
import { selectorIsLayoutFixed } from '../../../reducers/gerenciadorPrecos/isLayoutFixed';
import { SetupFarmaFormValues } from '../../Administrador/Produtos/Components/SetupFarma/schema';
import { ExpandedRowWrapper, TablePagination } from './Components';
import BigNumbersArea from './Components/BigNumbersArea/BigNumbersArea';
import EngineWarning from './Components/EngineWarning/EngineWarning';
import GerenciadorHeader from './Components/GerenciadorHeader/GerenciadorHeader';
import GerenciadorTableHeading from './Components/GerenciadorTableHeading/GerenciadorTableHeading';
import { ModalsWrapper } from './Components/ModalsWrapper';
import PriceManagerContextMenu from './Components/PriceManagerContextMenu/PriceManagerContextMenu';
import { PriceManagerViewBootstrap } from './PriceManagerViewBootstrap';
import TableGerenciador from './TableGerenciador';
import styles from './gerenciadorPrecos.module.scss';
import { useGerenciadorPrecos } from './hooks';
import { handleMoveFocusToNextRowInput } from './utils';

const MemoRowExpanded = memo(ExpandedRowWrapper);
const MemoPagination = memo(TablePagination);

const RevisaoPrecosContent = () => {
  const isLayoutFixed = useSelector(selectorIsLayoutFixed);

  const formMethodsIpaFarma = useForm<SetupFarmaFormValues>({
    defaultValues: {
      usePMCForIPAPricing: false,
    },
  });

  const {
    table: {
      data: content,
      expandedRowKeys,
      expandedRowOptions,
      onChangeCheckAll,
      handleContextMenu,
      handleBlur,
      handleRestoreRow,
      handleUpdateCampo,
      onSortColumn,
      selectedDatapoints: { isAllChecked, isIndeterminate, handleCheck, isToggleChecked },
      ...table
    },
    MODEL_GET_DATAPOINTS,
  } = useGerenciadorPrecos();

  useEffect(() => {
    searchSegmentos();
  }, []);

  useEffect(() => {
    const tableContainer = document.getElementById('table-revisao-precos');
    if (tableContainer) {
      const handleKeyDown = (e: KeyboardEvent) => {
        handleMoveFocusToNextRowInput(e);
      };

      tableContainer.addEventListener('keydown', handleKeyDown);

      return () => {
        tableContainer.removeEventListener('keydown', handleKeyDown);
      };
    }
  }, []);

  return (
    <>
      <main className={classNames('revisao-page', styles['layout'], isLayoutFixed && styles['layout-fixed'], styles['gerenciador-page'])}>
        <section className={styles['layout__fixed']}>
          <GerenciadorHeader />
          <div className={styles['layout__fixed__content']}>
            <EngineWarning />
            <BigNumbersArea className={styles['gerenciador-page__big-numbers']} priceManagerDatapointsModel={MODEL_GET_DATAPOINTS} />
            <NewFilterBox />
            <GerenciadorTableHeading />
          </div>
        </section>

        <section className={classNames(isLayoutFixed && 'hide-scroll', isLayoutFixed && styles['layout__scroll'])}>
          <FormProvider {...formMethodsIpaFarma}>
            <PriceManagerContextMenu>
              <TableGerenciador
                data={content}
                isAllChecked={isAllChecked}
                isIndeterminate={isIndeterminate}
                onRowContextMenu={handleContextMenu}
                onChangeCheckAll={onChangeCheckAll}
                handleBlur={handleBlur}
                handleRestoreRow={handleRestoreRow}
                handleCheck={handleCheck}
                handleUpdateCampo={handleUpdateCampo}
                isToggleChecked={isToggleChecked}
                expandedRowKeys={expandedRowKeys}
                onSortColumn={onSortColumn}
                renderRowExpanded={(rowData?: GerenciadorPrecos.RowData) => <MemoRowExpanded rowData={rowData} />}
                {...table}
              />
            </PriceManagerContextMenu>
          </FormProvider>
          <MemoPagination />
        </section>
      </main>

      <ModalsWrapper />
    </>
  );
};

const RevisaoPrecos = () => (
  <PriceManagerViewBootstrap>
    <RevisaoPrecosContent />
  </PriceManagerViewBootstrap>
);

export default RevisaoPrecos;
